// components/notifications/ScheduleEditorModal.jsx
import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ScheduleEditorModal = ({ rule, onSave, onClose }) => {
  const [scheduleType, setScheduleType] = useState(rule.schedule_type);
  const [time, setTime] = useState(rule.schedule_config.time || '09:00');
  const [dayOfWeek, setDayOfWeek] = useState(rule.schedule_config.day_of_week || 'Monday');
  const [monthDay, setMonthDay] = useState(rule.schedule_config.day || 1);
  const [twiceMonthlyDays, setTwiceMonthlyDays] = useState(
    rule.schedule_config.days || [1, 15]
  );
  const [intervalDays, setIntervalDays] = useState(rule.schedule_config.interval_days || 3);

  const handleSave = () => {
    let scheduleConfig = {
      time,
      timezone: 'America/Los_Angeles',
    };

    switch (scheduleType) {
      case 'daily':
        // Config only needs time
        break;
      
      case 'weekly':
        scheduleConfig.day_of_week = dayOfWeek;
        break;
      
      case 'monthly':
        scheduleConfig.day = monthDay;
        break;
      
      case 'twice_monthly':
        scheduleConfig.days = twiceMonthlyDays;
        break;
      
      case 'every_n_days':
        scheduleConfig.interval_days = intervalDays;
        break;
      
      default:
        break;
    }

    onSave(scheduleType, scheduleConfig);
  };

  const handleTwiceMonthlyDayToggle = (day) => {
    setTwiceMonthlyDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      } else {
        return [...prev, day].sort((a, b) => a - b);
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Schedule
              </h2>
              <p className="text-sm text-gray-600 mt-1">{rule.rule_name}</p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 text-gray-400 hover:text-gray-500 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Schedule Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Type
              </label>
              <select
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly (1st of month)</option>
                <option value="twice_monthly">Twice Monthly (1st & 15th)</option>
                <option value="every_n_days">Every N Days</option>
              </select>
            </div>

            {/* Weekly: Day of Week */}
            {scheduleType === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Week
                </label>
                <select
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
              </div>
            )}

            {/* Monthly: Day of Month */}
            {scheduleType === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Month
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={monthDay}
                  onChange={(e) => setMonthDay(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Twice Monthly: Days */}
            {scheduleType === 'twice_monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Run on Days (select 2)
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 5, 10, 15, 20, 25].map(day => (
                    <label
                      key={day}
                      className={`flex items-center justify-center px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                        twiceMonthlyDays.includes(day)
                          ? 'bg-blue-100 border-blue-500 text-blue-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={twiceMonthlyDays.includes(day)}
                        onChange={() => handleTwiceMonthlyDayToggle(day)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{day}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {twiceMonthlyDays.join(', ')}
                </p>
              </div>
            )}

            {/* Every N Days: Interval */}
            {scheduleType === 'every_n_days' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Run Every N Days
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Rule will run every {intervalDays} day{intervalDays !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time
              </label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Timezone: America/Los Angeles (PST/PDT)
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Save Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleEditorModal;